import { useState } from "react"
import { Award, TrendingUp, Users, DollarSign, Calendar } from "lucide-react"
import { useGetEarlyAdoptersQuery, useGetEarlyAdopterStatsQuery } from "../features/admin/adminApi"

const tierConfig = {
  founder: {
    icon: '🏆',
    label: 'Founding Member',
    color: 'text-yellow-600',
    bg: 'bg-yellow-100',
    border: 'border-yellow-500',
  },
  pioneer: {
    icon: '🌟',
    label: 'Pioneer',
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    border: 'border-purple-500',
  },
  explorer: {
    icon: '⭐',
    label: 'Early Adopter',
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    border: 'border-blue-500',
  },
}

function StatCard({ title, value, subtitle, icon: Icon, color }: any) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg ${color.bg}`}>
          <Icon className={color.text} size={24} />
        </div>
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-1">{value}</h3>
      <p className="text-sm font-medium text-gray-600">{title}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  )
}

export default function EarlyAdoptersPage() {
  const [page, setPage] = useState(1)
  const [tierFilter, setTierFilter] = useState<string>("")

  const { data: statsData } = useGetEarlyAdopterStatsQuery()
  const { data, isLoading } = useGetEarlyAdoptersQuery({ page, tier: tierFilter || undefined })

  const stats = statsData ?? {
    totalEarlyAdopters: 0,
    founderCount: 0,
    pioneerCount: 0,
    explorerCount: 0,
    totalRevenue: 0,
    totalOrders: 0,
    avgRevenuePerBusiness: 0,
    programEnabled: false,
    maxCount: 500,
    cutoffDate: '',
    remainingSlots: 500,
  }

  const earlyAdopters = data?.earlyAdopters ?? []
  const totalPages = data?.totalPages ?? 1

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Early Adopter Program</h1>
          <p className="text-sm text-gray-600 mt-1">
            Lifetime free subscriptions for the first {stats.maxCount} businesses
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className={`px-3 py-1.5 rounded-lg text-sm font-medium ${stats.programEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
            {stats.programEnabled ? 'Active' : 'Inactive'}
          </div>
          <div className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium">
            {stats.remainingSlots} slots remaining
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Total Early Adopters"
          value={stats.totalEarlyAdopters}
          subtitle={`of ${stats.maxCount} max`}
          icon={Users}
          color={{ text: 'text-blue-600', bg: 'bg-blue-100' }}
        />
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats.totalRevenue)}
          subtitle={`${stats.totalOrders} orders`}
          icon={DollarSign}
          color={{ text: 'text-green-600', bg: 'bg-green-100' }}
        />
        <StatCard
          title="Average per Business"
          value={formatCurrency(stats.avgRevenuePerBusiness)}
          subtitle="Revenue generated"
          icon={TrendingUp}
          color={{ text: 'text-purple-600', bg: 'bg-purple-100' }}
        />
        <StatCard
          title="Program Deadline"
          value={stats.cutoffDate ? new Date(stats.cutoffDate).toLocaleDateString() : 'N/A'}
          subtitle="Registration closes"
          icon={Calendar}
          color={{ text: 'text-orange-600', bg: 'bg-orange-100' }}
        />
      </div>

      {/* Tier Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">🏆</span>
            <div>
              <h3 className="font-bold text-gray-900">Founding Members</h3>
              <p className="text-xs text-gray-600">First 50 businesses</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-yellow-700">{stats.founderCount}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">🌟</span>
            <div>
              <h3 className="font-bold text-gray-900">Pioneers</h3>
              <p className="text-xs text-gray-600">Businesses 51-200</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-purple-700">{stats.pioneerCount}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">⭐</span>
            <div>
              <h3 className="font-bold text-gray-900">Early Adopters</h3>
              <p className="text-xs text-gray-600">Businesses 201-500</p>
            </div>
          </div>
          <p className="text-3xl font-bold text-blue-700">{stats.explorerCount}</p>
        </div>
      </div>

      {/* Early Adopters Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex gap-2">
          <select
            value={tierFilter}
            onChange={(e) => { setTierFilter(e.target.value); setPage(1) }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Tiers</option>
            <option value="founder">🏆 Founding Members</option>
            <option value="pioneer">🌟 Pioneers</option>
            <option value="explorer">⭐ Early Adopters</option>
          </select>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading early adopters...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">Business</th>
                  <th className="px-4 py-3 text-left">Tier</th>
                  <th className="px-4 py-3 text-left">Owner</th>
                  <th className="px-4 py-3 text-left">Joined</th>
                  <th className="px-4 py-3 text-right">Revenue</th>
                  <th className="px-4 py-3 text-right">Orders</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {earlyAdopters.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                      No early adopters found.
                    </td>
                  </tr>
                ) : (
                  earlyAdopters.map((adopter) => {
                    const config = tierConfig[adopter.tier]
                    return (
                      <tr key={adopter.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-gray-900">{adopter.businessName}</div>
                            <div className="text-xs text-gray-500">{adopter.id}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${config.bg} ${config.color} border ${config.border}`}>
                            <span>{config.icon}</span>
                            <span>{config.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-gray-700">{adopter.ownerName}</div>
                            <div className="text-xs text-gray-500">{adopter.ownerEmail}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {new Date(adopter.joinedAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900">
                          {formatCurrency(adopter.totalRevenue)}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-600">
                          {adopter.orderCount}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <Award className="text-blue-600 flex-shrink-0" size={20} />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">About the Early Adopter Program</p>
            <p className="text-blue-800">
              Early adopters receive a <strong>lifetime free subscription</strong> with unlimited products, services,
              and all premium features. They only pay the platform's <strong>10% commission on sales</strong>.
              No monthly fees, ever!
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
